import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface CoachProfile {
  id: string;
  user_id: string;
  organization_name: string | null;
  primary_sport: string;
}

export interface Team {
  id: string;
  coach_id: string;
  name: string;
  sport: string;
  join_code: string;
  created_at: string;
}

export interface TeamMember {
  id: string;
  team_id: string;
  player_user_id: string;
  joined_at: string;
  // Joined from profiles
  username?: string;
  display_name?: string;
  xp?: number;
  level?: number;
  streak?: number;
  completed_lessons?: string[];
  last_active_date?: string;
}

export function useCoach() {
  const { user } = useAuth();
  const [coachProfile, setCoachProfile] = useState<CoachProfile | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCoach, setIsCoach] = useState(false);

  const fetchCoachProfile = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    const { data } = await supabase
      .from('coach_profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    
    if (data) {
      setCoachProfile(data as CoachProfile);
      setIsCoach(true);
    } else {
      setIsCoach(false);
    }
    setLoading(false);
  }, [user]);

  const fetchTeams = useCallback(async () => {
    if (!coachProfile) return;
    const { data } = await supabase
      .from('teams')
      .select('id, coach_id, name, sport, created_at')
      .eq('coach_id', coachProfile.id)
      .order('created_at', { ascending: false });

    if (data) {
      // join_code is column-protected; fetch via secure RPC for the coach owner.
      const withCodes = await Promise.all(
        data.map(async (t) => {
          const { data: code } = await supabase.rpc('get_team_join_code' as any, { p_team_id: t.id });
          return { ...t, join_code: (code as string) ?? '' } as Team;
        })
      );
      setTeams(withCodes);
    }
  }, [coachProfile]);

  useEffect(() => { fetchCoachProfile(); }, [fetchCoachProfile]);
  useEffect(() => { if (coachProfile) fetchTeams(); }, [coachProfile, fetchTeams]);

  const createTeam = async (name: string, sport: string) => {
    if (!coachProfile) return null;
    const { data, error } = await supabase
      .from('teams')
      .insert({ coach_id: coachProfile.id, name, sport })
      .select('id, coach_id, name, sport, created_at')
      .single();

    if (error) throw error;
    const { data: code } = await supabase.rpc('get_team_join_code' as any, { p_team_id: data.id });
    await fetchTeams();
    return { ...data, join_code: (code as string) ?? '' } as Team;
  };

  const deleteTeam = async (teamId: string) => {
    const { error } = await supabase.from('teams').delete().eq('id', teamId);
    if (error) throw error;
    await fetchTeams();
  };

  const fetchTeamMembers = async (teamId: string): Promise<TeamMember[]> => {
    const { data: members } = await supabase
      .from('team_members')
      .select('*')
      .eq('team_id', teamId);
    
    if (!members || members.length === 0) return [];

    // Fetch profile + progress data for each member
    const userIds = members.map(m => m.player_user_id);
    const [{ data: profiles }, { data: progress }] = await Promise.all([
      supabase.rpc('get_coach_team_member_profiles' as any, { p_user_ids: userIds }),
      supabase.from('user_progress').select('user_id, xp, level, streak, completed_lessons, last_active_date').in('user_id', userIds),
    ]);

    return members.map(m => {
      const prof = (profiles as any[] | null)?.find(p => p.user_id === m.player_user_id);
      const prog = progress?.find(p => p.user_id === m.player_user_id);
      return {
        ...m,
        username: prof?.username,
        display_name: undefined,
        xp: prog?.xp ?? 0,
        level: prog?.level ?? 0,
        streak: prog?.streak ?? 0,
        completed_lessons: prog?.completed_lessons ?? [],
        last_active_date: prog?.last_active_date,
      } as TeamMember;
    });
  };

  // NOTE: coaches cannot add members directly. Players join with the team join
  // code (join_team_by_code) or a parent enrolls their child
  // (parent_join_child_to_team), so roster membership always requires consent.

  const removeMember = async (memberId: string) => {
    const { error } = await supabase.from('team_members').delete().eq('id', memberId);
    if (error) throw error;
  };


  return {
    coachProfile,
    teams,
    loading,
    isCoach,
    createTeam,
    deleteTeam,
    fetchTeamMembers,
    removeMember,
    refetch: fetchCoachProfile,
  };
}
